import BaseApi from "./BaseApi";
import { logError } from "../utils/errorLogger";
import { unwrapPaginatedResponse, buildPaginationParams } from "../utils/paginatedApi";
import moment from 'moment'
class ReservationApi extends BaseApi {
  constructor() {
    super("hotel-reservation"); // Set the endpoint to "reservations"
  }

  // Create a new reservation
  async createReservation(data) {
    try {
      const response = await this.getApiInstance(true).post(
        `/${this.endpoint}`,
        data
      );
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  async createDevis(data) {
    try {
      const response = await this.getApiInstance(true).post(
        `/${this.endpoint}/devis`,
        data
      );
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  async confirmDevis(data) {
    try {
      const response = await this.getApiInstance(true).post(
        `/${this.endpoint}/confirm-devis`,
        data
      );
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  async getPartnerBookingIds() {
    try {
      const response = await this.getApiInstance(true).get(`/${this.endpoint}/partner-booking-ids`);
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      logError(error);
      return [];
    }
  }

  // Get all reservations
  async getAllReservations() {
    try {
      const response = await this.getApiInstance(true).get(`/${this.endpoint}`);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  async getReservationsPage({ page = 1, limit = 10, filters = {} } = {}) {
    try {
      const response = await this.getApiInstance(true).get(`/${this.endpoint}`, {
        params: buildPaginationParams({ page, limit, filters }),
      });
      const unwrapped = unwrapPaginatedResponse(response.data);
      return {
        ...unwrapped,
        stats: response.data?.stats,
      };
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Get filtered reservations
  async getFilteredReservations(filters) {
    try {
      const response = await this.getApiInstance(true).post(
        `/${this.endpoint}/filter`,
        filters
      );
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Get a single reservation by ID
  async getReservationById(id) {
    try {
      const response = await this.getApiInstance(true).get(
        `/${this.endpoint}/${id}`
      );
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Update a reservation by ID
  async updateReservation(id, data) {
    try {
      const response = await this.getApiInstance(true).put(
        `/${this.endpoint}/${id}`,
        data
      );
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Delete a reservation by ID
  async deleteReservation(id) {
    try {
      const response = await this.getApiInstance(true).delete(
        `/${this.endpoint}/${id}`
      );
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }
   async sendVoucher(data) {
    try {
      const response = await this.getApiInstance(true).post(`/${this.endpoint}/send-voucher`, data);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }
  async getReservationStats() {
    try {
      const response = await this.getApiInstance(true).get(`/${this.endpoint}/stats`);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Search hotels with reservation parameters
  async searchHotels(searchParams) {
    try {
      const response = await this.getApiInstance(true).post(
        `/${this.endpoint}/search`,
        searchParams
      );
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  async searchMoteurTgxProgressive(searchParams = {}, page = 1) {
    try {
      const response = await this.getApiInstance(true).post(
        `/${this.endpoint}/tgx-progressive-search`,
        { ...searchParams, page },
        { timeout: 180000 }
      );
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  async searchMoteurTgxHotelOptions(searchParams = {}) {
    try {
      const response = await this.getApiInstance(true).post(
        `/${this.endpoint}/tgx-hotel-options`,
        searchParams,
        { timeout: 60000 }
      );
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  async fetchMoteurTgxHotelsContent(codes = [], context = {}) {
    const unique = [...new Set(
      (codes || []).map((code) => String(code || '').trim()).filter(Boolean)
    )];
    if (!unique.length) return {};

    const response = await this.getApiInstance(true).post(
      `/${this.endpoint}/tgx-hotels/content`,
      {
        codes: unique.slice(0, 50),
        destinationName: context.destinationName || context.destination || undefined,
        country: context.country || undefined,
        paysId: context.paysId ?? undefined,
      },
      { timeout: 60000 }
    );
    return response.data?.hotelMeta || {};
  }

  async fetchMoteurTgxHotelContent(hotelCode, context = {}) {
    const code = String(hotelCode || '').trim();
    if (!code) {
      return { hotelCode: '', name: null, media: [] };
    }
    const response = await this.getApiInstance(true).get(
      `/${this.endpoint}/tgx-hotel/${encodeURIComponent(code)}/content`,
      {
        params: {
          destinationName: context.destinationName || context.destination || undefined,
          country: context.country || undefined,
          paysId: context.paysId ?? undefined,
        },
        timeout: 30000,
      }
    );
    return response.data;
  }

  async fetchTgxSearchPage(sessionId, params = {}) {
    try {
      const response = await this.getApiInstance(true).get(
        `/${this.endpoint}/tgx-search/${sessionId}`,
        { params }
      );
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  async fetchTgxSearchStatus(sessionId) {
    try {
      const response = await this.getApiInstance(true).get(
        `/${this.endpoint}/tgx-search/${sessionId}/status`
      );
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  async deleteTgxSearchSession(sessionId, cleanupToken = null) {
    try {
      await this.getApiInstance(true).delete(
        `/${this.endpoint}/tgx-search/${sessionId}`,
        { params: cleanupToken ? { token: cleanupToken } : {} }
      );
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Approve or reject reservation
  async approveReservation(reservationId, status) {
    try {
      const response = await this.getApiInstance(true).post(
        `/${this.endpoint}/approve/${reservationId}`,
        { status }
      );
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }
  async notifyHotelStatusChange(data) {
    try {
      const response = await this.getApiInstance(true).post(
        `/${this.endpoint}/notify-hotel-status-change`,
        data
      );
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }
  
  async annulerReservation(reservationId) {
    try {
      const response = await this.getApiInstance(true).post(
        `/${this.endpoint}/annuler/${reservationId}`
      );
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  /**
   * Cancel external booking (Cyberesa or Octasoft)
   * @param {number} reservationId - Local reservation ID
   * @param {Object} params - Additional parameters
   * @param {number} params.userId - User ID for Cyberesa (default: 23866)
   * @param {number} params.languageId - Language ID for Cyberesa (default: 1)
   * @returns {Promise} Response with cancellation result
   */
  async cancelExternalBooking(reservationId, params = {}) {
    try {
      const response = await this.getApiInstance(true).post(
        `/${this.endpoint}/cancel-external/${reservationId}`,
        params
      );
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Generate HTML voucher for a reservation
  async generateHtmlVoucher(reservationId, language = 'fr') {
    try {
      const response = await this.getApiInstance(true).get(
        `/${this.endpoint}/voucher/html/${reservationId}?lang=${language}`
      );
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Download PDF voucher for a reservation
  async downloadPdfVoucher(reservationId, language = 'fr') {
    try {
      const response = await this.getApiInstance(true).get(
        `/${this.endpoint}/voucher/pdf/${reservationId}?lang=${language}`,
        { responseType: 'blob' }
      );
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }
  async sendVoucherFromDetails(reservationId, name, language = 'fr') {
    try {
      const response = await this.getApiInstance(true).post(`/${this.endpoint}/send-voucher-from-details`, { reservationId, name, language });
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Retry TunisiaBeds booking creation
  async retryTunisiaBedsBooking(reservationId) {
    try {
      const response = await this.getApiInstance(true).post(
        `/${this.endpoint}/retry-tunisia-beds/${reservationId}`
      );
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Get cancellation summary before confirmation
  async getCancellationSummary(reservationId) {
    try {
      const response = await this.getApiInstance(true).get(
        `/${this.endpoint}/${reservationId}/cancellation-summary`
      );
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Cancel reservation with full processing
  async cancelReservation(reservationId) {
    try {
      const response = await this.getApiInstance(true).post(
        `/${this.endpoint}/${reservationId}/cancel`
      );
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Select/deselect event for a room
  async selectEvenement(possibleChambre, operation, extra = {}) {
    try {
      const response = await this.getApiInstance(true).post(
        `/${this.endpoint}/select-evenement`,
        {
          possibleChambres: possibleChambre,
          operation, // "in" or "out"
          eventId: extra.eventId,
          zoneId: extra.zoneId,
          supplementIds: extra.supplementIds,
        }
      );
      // Extract possibleChambres from response (response.data.possibleChambres)
      return response.data?.possibleChambres || response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Select/deselect extra for a room
  async selectExtraChambre(possibleChambre, operation) {
    try {
      const response = await this.getApiInstance(true).post(
        `/${this.endpoint}/select-extra-chambre`,
        {
          possibleChambres: possibleChambre,
          operation, // "in" or "out"
        }
      );
      // Extract possibleChambres from response (response.data.possibleChambres)
      return response.data?.possibleChambres || response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Get economic models for the logged-in user's partner
  async getUserEconomicModels(dateCheckin, dateCheckout, dateSejour = null) {
    try {
      // Use today's date as date_sejour if not provided
      const sejourDate = dateSejour || moment().format('YYYY-MM-DD');
      
      const params = new URLSearchParams({
        date_checkin: dateCheckin,
        date_checkout: dateCheckout,
        date_sejour: sejourDate
      });
      
      const response = await this.getApiInstance(true).get(
        `/${this.endpoint}/user/economic-models?${params.toString()}`
      );
      return response.data?.data || response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }
}

export default new ReservationApi();
